import { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  Modal,
  TouchableOpacity,
  Image,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { toast, ToastContainer } from 'react-toastify';
import AsyncStorage from '@react-native-async-storage/async-storage';
import API_PATHS from './apiCalls/ApiPath';

export default function ChildDetails({ kid, visible, setVisible, onUpdateSuccess }) {
  const [saving, setSaving]                 = useState(false);
  const [name, setName]                     = useState('');
  const [age, setAge]                       = useState('');
  const [gender, setGender]                 = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [allergy, setAllergy]               = useState('');
  const [healthIssue, setHealthIssue]       = useState('');
  const [profilePic, setProfilePic]         = useState(null);

  // Pre-fill fields whenever kid data changes
  useEffect(() => {
    if (kid) {
      setName(kid.name || '');
      setAge(kid.age?.toString() || '');
      setGender(kid.gender || '');
      setEmergencyPhone(kid.emergencyPhone || '');
      setAllergy(kid.allergy || '');
      setHealthIssue(kid.healthIssue || '');
      setProfilePic(kid.profilePic || null);
    }
  }, [kid]);

  if (!kid) return null;

  // ── Pick image ───────────────────────────────────────────────────────────
  const pickImage = () => {
    if (Platform.OS === 'web') {
      // Web — use browser file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          const uri = URL.createObjectURL(file);
          setProfilePic(uri);
        }
      };
      input.click();
    } else {
      // Mobile — require inside function so webpack doesn't bundle it on web
      const { launchImageLibrary } = require('react-native-image-picker');
      launchImageLibrary({ mediaType: 'photo', quality: 0.8 }, (response) => {
        if (!response.didCancel && !response.errorCode && response.assets?.[0]) {
          setProfilePic(response.assets[0].uri);
        }
      });
    }
  };

  // ── Upload image to server ───────────────────────────────────────────────
  const uploadImage = async (uri) => {
    const formData = new FormData();

    if (Platform.OS === 'web') {
      const response = await fetch(uri);
      const blob = await response.blob();
      formData.append('file', blob, 'profile.jpg');
    } else {
      formData.append('file', { uri, type: 'image/jpeg', name: 'profile.jpg' });
    }

    const token = await AsyncStorage.getItem('token');
    const clientToken = await AsyncStorage.getItem('clientToken');

    const response = await fetch(`${API_PATHS.BASE_URL}${API_PATHS.UPLOAD_FILE}`, {
      method: 'POST',
      body: formData,
      headers: { Authorization: `Bearer ${clientToken || token}` },
    });

    const result = await response.json();
    if (result.Success === 1) return result.Data.url;
    throw new Error(result.Message || 'Image upload failed');
  };

  // ── Save / update kid ────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!name.trim() || !age || !gender || !emergencyPhone) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setSaving(true);
      console.log('kid data:', kid);
      const token = await AsyncStorage.getItem('token');

      let imageUrl = profilePic;
      if (profilePic && !profilePic.startsWith('http')) {
        imageUrl = await uploadImage(profilePic);
        if (!imageUrl) {
          toast.error('Image upload failed. Try again.');
          return;
        }
      }

      const body = {
        kidId: kid.kidId,
        name,
        gender,
        age: parseInt(age),
        emergencyPhone,
        healthIssue,
        allergy,
        profilePic: imageUrl,
      };

      const response = await fetch(
        `${API_PATHS.BASE_URL}v1/parent/edit-kid-details`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      );

      const result = await response.json();
      console.log('edit kid result:', result);

      if (result.Success === 1) {
        toast.success('Kid updated successfully!');
        if (onUpdateSuccess) onUpdateSuccess();
        setTimeout(() => {
          setVisible(false);
        }, 1500);
      } else {
        toast.error(result.Message || 'Failed to update kid');
      }
    } catch (error) {
      console.error('handleSave error:', error);
      toast.error('Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={visible}
      onRequestClose={() => setVisible(false)}
    >
      <View style={styles.centeredView}>
        <View style={styles.modalView}>

          {/* Close */}
          <TouchableOpacity style={styles.closeBtn} onPress={() => setVisible(false)}>
            <Text style={styles.closeBtnText}>✕</Text>
          </TouchableOpacity>

          <ScrollView
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {/* Profile Picture */}
            <TouchableOpacity onPress={pickImage} style={styles.avatarWrapper}>
              <Image
                style={styles.avatar}
                source={
                  profilePic
                    ? { uri: profilePic }
                    : require('./assets/AddKidPic.png')
                }
              />
              <View style={styles.editOverlay}>
                <Text style={styles.editOverlayText}>Edit</Text>
              </View>
            </TouchableOpacity>

            {/* Name */}
            <Text style={styles.label}>NAME *</Text>
            <TextInput
              style={styles.input}
              value={name}
              onChangeText={setName}
              placeholder="Child's name"
              placeholderTextColor="#aaa"
            />

            {/* Age */}
            <Text style={styles.label}>AGE *</Text>
            <TextInput
              style={styles.input}
              value={age}
              onChangeText={setAge}
              placeholder="Age"
              placeholderTextColor="#aaa"
              keyboardType="numeric"
            />

            {/* Gender */}
            <Text style={styles.label}>GENDER *</Text>
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={gender}
                onValueChange={setGender}
                style={[styles.picker, { color: gender ? '#000' : '#aaa' }]}
              >
                <Picker.Item label="Select Gender" value="" color="#aaa" />
                <Picker.Item label="Male"   value="BOY"  color="#000" />
                <Picker.Item label="Female" value="GIRL" color="#000" />
              </Picker>
            </View>

            {/* Emergency Number */}
            <Text style={styles.label}>EMERGENCY NUMBER *</Text>
            <TextInput
              style={styles.input}
              value={emergencyPhone}
              onChangeText={setEmergencyPhone}
              placeholder="Emergency phone"
              placeholderTextColor="#aaa"
              keyboardType="phone-pad"
            />

            {/* Allergy */}
            <Text style={styles.label}>ALLERGY</Text>
            <TextInput
              style={styles.input}
              value={allergy}
              onChangeText={setAllergy}
              placeholder="Any allergies"
              placeholderTextColor="#aaa"
            />

            {/* Health Issue */}
            <Text style={styles.label}>HEALTH ISSUE</Text>
            <TextInput
              style={[styles.input, styles.multiline]}
              value={healthIssue}
              onChangeText={setHealthIssue}
              placeholder="Any health issues"
              placeholderTextColor="#aaa"
              multiline
              numberOfLines={3}
            />

            {/* Save Button */}
            {saving ? (
              <ActivityIndicator size="large" color="#A6CE39" style={{ marginTop: 16 }} />
            ) : (
              <TouchableOpacity style={styles.saveBtn} onPress={handleSave}>
                <Text style={styles.saveBtnText}>Save Changes</Text>
              </TouchableOpacity>
            )}

          </ScrollView>

          {/* ToastContainer always mounted inside modal */}
          <ToastContainer position="top-center" autoClose={1500} />

        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalView: {
    margin: 20,
    backgroundColor: '#fff',
    borderRadius: 16,
    paddingTop: 44,
    paddingHorizontal: 20,
    paddingBottom: 20,
    width: '90%',
    maxHeight: '90%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
    elevation: 5,
  },
  closeBtn: {
    position: 'absolute',
    top: 12,
    right: 14,
    zIndex: 100,
    padding: 6,
  },
  closeBtnText: {
    fontSize: 18,
    color: '#888',
    fontWeight: 'bold',
  },
  scrollContent: {
    alignItems: 'center',
    paddingBottom: 10,
  },

  // Avatar
  avatarWrapper: {
    width: 100,
    height: 100,
    borderRadius: 50,
    marginBottom: 20,
    overflow: 'hidden',
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
  },
  editOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    paddingVertical: 3,
  },
  editOverlayText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
  },

  // Form
  label: {
    alignSelf: 'flex-start',
    fontSize: 11,
    color: '#888',
    letterSpacing: 0.8,
    marginBottom: 4,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  input: {
    width: '100%',
    height: 48,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 15,
    color: '#333',
    backgroundColor: '#fff',
    marginBottom: 12,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  multiline: {
    height: 80,
    textAlignVertical: 'top',
    paddingTop: 10,
  },
  pickerContainer: {
    width: '100%',
    height: 48,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    marginBottom: 12,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  picker: {
    width: '100%',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },

  // Save button
  saveBtn: {
    width: '100%',
    height: 50,
    backgroundColor: '#A6CE39',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  saveBtnText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
});